import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { MoreVertical, Plus, Send, Trash2, UserCheck, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * CollaboratorsList component
 * Shows all collaborators and pending invitations for a festival
 * Allows owner to add, remove, and resend invitations
 */
const CollaboratorsList = ({ festivalId }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const queryClient = useQueryClient();

  // Fetch collaborators and invitations
  const { data, isLoading, error } = useQuery({
    queryKey: ['collaborators', festivalId],
    queryFn: async () => {
      const response = await fetch(`/api/festivals/${festivalId}/collaborators`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch collaborators');
      return response.json();
    }
  });

  // Add collaborator mutation
  const addMutation = useMutation({
    mutationFn: async (email) => {
      const response = await fetch(`/api/festivals/${festivalId}/collaborators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add collaborator');
      }
      return response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries(['collaborators', festivalId]);
      setShowAddDialog(false);
      setNewEmail('');
      toast.success(result.message);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  // Remove collaborator mutation
  const removeMutation = useMutation({
    mutationFn: async (collaboratorId) => {
      const response = await fetch(`/api/festivals/${festivalId}/collaborators/${collaboratorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to remove collaborator');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['collaborators', festivalId]);
      toast.success('Collaborator removed');
    },
    onError: () => {
      toast.error('Failed to remove collaborator');
    }
  });

  // Cancel invitation mutation
  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId) => {
      const response = await fetch(`/api/festivals/${festivalId}/collaborators/invite/${inviteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to cancel invitation');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['collaborators', festivalId]);
      toast.success('Invitation cancelled');
    },
    onError: () => {
      toast.error('Failed to cancel invitation');
    }
  });

  // Resend invitation mutation
  const resendMutation = useMutation({
    mutationFn: async (inviteId) => {
      const response = await fetch(`/api/festivals/${festivalId}/collaborators/invite/${inviteId}/resend`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to resend invitation');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['collaborators', festivalId]);
      toast.success('Invitation resent');
    },
    onError: () => {
      toast.error('Failed to resend invitation');
    }
  });

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (newEmail.trim()) {
      addMutation.mutate(newEmail.trim());
    }
  };

  if (isLoading) {
    return <div style={{ color: '#666', padding: '20px' }}>Loading collaborators...</div>;
  }

  if (error) {
    return <div style={{ color: '#dc3545', padding: '20px' }}>Error loading collaborators</div>;
  }

  const { collaborators = [], invitations = [], isOwner } = data;

  return (
    <div>
      <h2 style={{ color: '#333', marginBottom: '20px' }}>Collaborators</h2>
      
      {/* Collaborators List */}
      <div style={{ marginBottom: '20px' }}>
        {collaborators.length === 0 && invitations.length === 0 && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No collaborators yet.</p>
        )}
        
        {/* Active collaborators */}
        {collaborators.map((collab) => (
          <CollaboratorRow
            key={`collab-${collab.id}`}
            id={collab.id}
            email={collab.email}
            name={collab.name}
            status="active"
            isOwner={isOwner}
            role={collab.role}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            onRemove={() => {
              if (window.confirm(`Remove ${collab.name || collab.email} as a collaborator?`)) {
                removeMutation.mutate(collab.id);
              }
            }}
          />
        ))}

        {/* Pending invitations */}
        {invitations.map((invite) => (
          <CollaboratorRow
            key={`invite-${invite.id}`}
            id={`invite-${invite.id}`}
            email={invite.email}
            status="pending"
            isOwner={isOwner}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            onResend={() => resendMutation.mutate(invite.id)}
            onRemove={() => {
              if (window.confirm(`Cancel invitation to ${invite.email}?`)) {
                cancelInviteMutation.mutate(invite.id);
              }
            }}
          />
        ))}
      </div>

      {/* Add Collaborator Button - only for owner */}
      {isOwner && (
        <button
          onClick={() => setShowAddDialog(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          <Plus size={16} />
          Add Collaborator
        </button>
      )}

      {/* Add Collaborator Dialog */}
      {showAddDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Collaborator</h3>
            <form onSubmit={handleAddSubmit}>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  boxSizing: 'border-box'
                }}
                autoFocus
                required
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowAddDialog(false); setNewEmail(''); }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#f0f0f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {addMutation.isPending ? 'Adding...' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CollaboratorRow - Individual row for a collaborator or pending invite
 */
const CollaboratorRow = ({
  id, email, name, status, role, isOwner,
  activeMenu, setActiveMenu,
  onResend, onRemove
}) => {
  const isActive = status === 'active';
  const menuId = id;
  const showMenu = activeMenu === menuId;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      borderBottom: '1px solid #eee',
      position: 'relative'
    }}>
      {/* Status icon */}
      <div style={{ marginRight: '12px' }}>
        {isActive ? (
          <UserCheck size={20} style={{ color: '#28a745' }} />
        ) : (
          <Clock size={20} style={{ color: '#ffc107' }} />
        )}
      </div>

      {/* Email/Name */}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, color: '#333' }}>
          {name || email}
        </div>
        {name && (
          <div style={{ fontSize: '12px', color: '#666' }}>{email}</div>
        )}
        {!isActive && (
          <div style={{ fontSize: '12px', color: '#ffc107', fontStyle: 'italic' }}>
            Pending invitation
          </div>
        )}
      </div>

      {/* Role badge (for active collaborators) */}
      {isActive && role && role !== 'creator' && (
        <span style={{
          padding: '4px 8px',
          backgroundColor: role === 'administrator' ? '#007bff' : '#6c757d',
          color: 'white',
          borderRadius: '4px',
          fontSize: '11px',
          marginRight: '12px',
          textTransform: 'capitalize'
        }}>
          {role}
        </span>
      )}
      {isActive && role === 'creator' && (
        <span style={{
          padding: '4px 8px',
          backgroundColor: '#28a745',
          color: 'white',
          borderRadius: '4px',
          fontSize: '11px',
          marginRight: '12px'
        }}>
          Owner
        </span>
      )}

      {/* Action menu button - only for owner and not for self (creator) */}
      {isOwner && role !== 'creator' && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setActiveMenu(showMenu ? null : menuId)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: '#666'
            }}
          >
            <MoreVertical size={18} />
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '160px',
              zIndex: 100,
              overflow: 'hidden'
            }}>
              {!isActive && onResend && (
                <button
                  onClick={() => { onResend(); setActiveMenu(null); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px'
                  }}
                >
                  <Send size={14} />
                  Resend invite
                </button>
              )}
              <button
                onClick={() => { onRemove(); setActiveMenu(null); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#dc3545',
                  fontSize: '14px'
                }}
              >
                <Trash2 size={14} />
                {isActive ? 'Remove' : 'Cancel invite'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollaboratorsList;

